import { DestroyRef, Directive, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { OrderFormGroup, OrderVolumeCalculation, TradeFacade, TradeService } from '@zero/trade/domain';
import { filter, firstValueFrom } from 'rxjs';

@Directive()
export class TradeConfirmPageBase {
  public isReady = false;

  public readonly tradeService = inject(TradeService);
  protected readonly activatedRoute = inject(ActivatedRoute);
  protected readonly router = inject(Router);
  readonly #destroyRef = inject(DestroyRef);
  public readonly tradeFacade = inject(TradeFacade);

  public async init(): Promise<void> {
    const cache = await firstValueFrom(this.tradeService.cache$);
    this.isReady = Boolean(cache?.form && cache?.asset);

    if (this.isReady) {
      this.tradeService.updateInstrument(cache.asset);
      this.#subscribeToQuote(cache.form);
    }

    this.tradeFacade.isOrderPlaced$
      .pipe(
        filter((isOrderPlaced) => !!isOrderPlaced),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(() => this.handleSuccessfulTrade());
  }

  public destroy(): void {
    this.tradeService.reset();
  }

  #subscribeToQuote = (form: FormGroup<OrderFormGroup>): void => {
    this.tradeService.quote$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((quote) => {
      const optionsForm = form.get('optionsForm');
      this.tradeService.updateOrderVolume({
        quote,
        execType: form.get('execType')?.value,
        direction: form.get('direction')?.value,
        quantity: form.get('quantityForm')?.get('quantity')?.value,
        stopPrice: optionsForm?.get('stopPrice')?.value,
        stopLimitPrice: optionsForm?.get('stopLimitPrice')?.value,
        limitPrice: optionsForm?.get('limitPrice')?.value,
        stopLoss: optionsForm?.get('stopLoss')?.value,
        trailingAbs: optionsForm?.get('trailingAbs')?.value,
        trailingRel: optionsForm?.get('trailingRel')?.value,
        trailingType: optionsForm?.get('trailingType')?.value,
        initialStopPrice: optionsForm?.get('initialStopPrice')?.value,
      } as OrderVolumeCalculation);
    });
  };

  public async placeOrder(confirmLowTrading = false): Promise<void> {
    confirmLowTrading && (await this.tradeService.confirmLowTrading());
    const isCombinedOrder = await firstValueFrom(this.tradeFacade.isCombinedOrderVerified$);
    isCombinedOrder ? this.tradeService.placeCombinedOrder() : this.tradeService.placeOrder();
  }

  public async confirmLowTradingAndPlaceOrder(): Promise<void> {
    await this.placeOrder(true);
  }

  protected handleSuccessfulTrade(): void {
    // nothing to do, the magic happens in the overridden 🧙🏻‍♀️
  }
}
